import { defineStore } from 'pinia'
import { KpiApi, type KpiResponse, type OpportunityKpiProjection, type KpiBodyDto } from '@/api-swagger'
import { computed, ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface MonthlyDataMetric {
  [month: string]: number
}

export interface UserMonthlyData {
  totalSales: MonthlyDataMetric
  orderCounts: MonthlyDataMetric
  averageOrderValues: MonthlyDataMetric
}

export interface KpiCommercial {
  id: number
  name: string
  email: string
}

export type KpiTab = 'saleOrder' | 'opportunity' | 'event' | 'call'

const KPI_TABS: KpiTab[] = ['saleOrder', 'opportunity', 'event', 'call']
const CACHE_TTL_MS = 5 * 60 * 1000
const GENERIC_ERROR = 'Une erreur est survenue. Veuillez actualiser la page.'

export const useKpiStore = defineStore('kpi', () => {
  const suiteStore = useSuiteStore()
  // TODO: supprimer quand GET /kpi/opportunities/me sera migré dans boostcore
  const legacyApi = new KpiApi()

  const kpiPost = async <T>(path: string, body: KpiBodyDto, signal?: AbortSignal): Promise<T> => {
    const res = await suiteStore.suiteFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    })
    if (!res) throw new Error(`KPI call failed: ${path}`)
    if (!res.ok) throw new Error(`KPI ${path} → HTTP ${res.status}`)
    return res.json() as Promise<T>
  }

  // Date range selectors
  const startDate = ref('')
  const endDate = ref('')
  const usersSelected = ref<number[]>([])
  const viewType = ref<'day' | 'week' | 'month' | 'year'>('month')
  const activeTab = ref<KpiTab>('saleOrder')

  const kpiCommercials = ref<KpiCommercial[]>([])

  const kpisOpportunities = ref<OpportunityKpiProjection[]>([])
  const kpiSaleOrderCreate = ref<KpiResponse[]>()
  const kpiSaleOrderSigned = ref<KpiResponse[]>()
  const kpiOpportunityWon = ref<KpiResponse[]>()
  const kpiOpportunityCreated = ref<KpiResponse[]>()
  const kpiEventCreated = ref<KpiResponse[]>()
  const kpiEventDone = ref<KpiResponse[]>()
  const kpiEventCampaign = ref<KpiResponse[]>()
  const kpiCallCreated = ref<KpiResponse[]>()

  const categoryColors = ref<Record<string, string>>({
    R1P: '#2b5876',
    R2P: '#5580a5',
    'R+P': '#7ea3c0',
    R1C: '#72d7c7',
    R2C: '#68ada2',
    'R+C': '#4a8877',
    Autres: '#95A5A6',
  })

  const loadingKpiOpp = ref(false)

  const loadingSaleOrderCreateKpi = ref(false)
  const loadingSaleOrderSignedKpi = ref(false)

  const loadingOpportunityWonKpi = ref(false)
  const loadingOpportunityCreatedKpi = ref(false)

  const loadingEventCreatedKpi = ref(false)
  const loadingEventDoneKpi = ref(false)
  const loadingEventCampaignKpi = ref(false)

  const loadingCallCreatedKpi = ref(false)

  const errorKpi = ref<string | null>(null)

  // Per-metric error state — cleared at fetch start, set on non-abort failure
  const errorSaleOrderCreate = ref<string | null>(null)
  const errorSaleOrderSigned = ref<string | null>(null)
  const errorOpportunityWon = ref<string | null>(null)
  const errorOpportunityCreated = ref<string | null>(null)
  const errorEventCreated = ref<string | null>(null)
  const errorEventDone = ref<string | null>(null)
  const errorEventCampaign = ref<string | null>(null)
  const errorCallCreated = ref<string | null>(null)

  // Cache freshness per tab (ms timestamp, 0 = invalid)
  const loadedAt = ref<Record<KpiTab, number>>({
    saleOrder: 0,
    opportunity: 0,
    event: 0,
    call: 0,
  })

  // AbortController per tab — a new fetch cancels the previous in-flight one
  const controllers: Record<KpiTab, AbortController | null> = {
    saleOrder: null,
    opportunity: null,
    event: null,
    call: null,
  }

  // Active request promise per tab — lets concurrent fetchKpiForTab callers
  // join the same in-flight request instead of cancelling and restarting it.
  const inflight: Record<KpiTab, Promise<void> | null> = {
    saleOrder: null,
    opportunity: null,
    event: null,
    call: null,
  }

  const isKpiLoading = computed(
    () =>
      loadingSaleOrderCreateKpi.value ||
      loadingSaleOrderSignedKpi.value ||
      loadingOpportunityWonKpi.value ||
      loadingOpportunityCreatedKpi.value ||
      loadingEventCreatedKpi.value ||
      loadingEventDoneKpi.value ||
      loadingEventCampaignKpi.value ||
      loadingCallCreatedKpi.value,
  )

  const isAbortError = (e: unknown): boolean => {
    if (!e) return false
    if (e instanceof DOMException && e.name === 'AbortError') return true
    const name = (e as { name?: string }).name
    return name === 'AbortError'
  }

  const buildBody = (): KpiBodyDto => ({
    users: usersSelected.value || [],
    startDate: startDate.value,
    endDate: endDate.value,
  })

  const cancelTab = (tab: KpiTab) => {
    controllers[tab]?.abort()
    controllers[tab] = null
    inflight[tab] = null
  }

  const cancelAll = () => {
    ;(Object.keys(controllers) as KpiTab[]).forEach(cancelTab)
  }

  // Invalidate all cached tabs — typically called when filters change.
  // Immediately clears data, resets errors, and flips every loading flag to
  // true so hidden tabs show a loader instead of their previous (stale)
  // content while the sequential prefetch is still running.
  const invalidateCaches = () => {
    cancelAll()
    loadedAt.value = { saleOrder: 0, opportunity: 0, event: 0, call: 0 }

    kpiSaleOrderCreate.value = []
    kpiSaleOrderSigned.value = []
    kpiOpportunityWon.value = []
    kpiOpportunityCreated.value = []
    kpiEventCreated.value = []
    kpiEventDone.value = []
    kpiEventCampaign.value = []
    kpiCallCreated.value = []

    errorSaleOrderCreate.value = null
    errorSaleOrderSigned.value = null
    errorOpportunityWon.value = null
    errorOpportunityCreated.value = null
    errorEventCreated.value = null
    errorEventDone.value = null
    errorEventCampaign.value = null
    errorCallCreated.value = null

    loadingSaleOrderCreateKpi.value = true
    loadingSaleOrderSignedKpi.value = true
    loadingOpportunityWonKpi.value = true
    loadingOpportunityCreatedKpi.value = true
    loadingEventCreatedKpi.value = true
    loadingEventDoneKpi.value = true
    loadingEventCampaignKpi.value = true
    loadingCallCreatedKpi.value = true
  }

  const isCacheFresh = (tab: KpiTab): boolean => {
    const ts = loadedAt.value[tab]
    return ts > 0 && Date.now() - ts < CACHE_TTL_MS
  }

  // Initialize date range with default values (current year)
  const initializeDateRange = () => {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    startDate.value = `${currentYear}-01-01`
    endDate.value = `${currentYear}-12-31`
  }

  initializeDateRange()

  const getSaleOrderKpi = async () => {
    cancelTab('saleOrder')
    const controller = new AbortController()
    controllers.saleOrder = controller
    const { signal } = controller

    loadingSaleOrderCreateKpi.value = true
    loadingSaleOrderSignedKpi.value = true
    errorSaleOrderCreate.value = null
    errorSaleOrderSigned.value = null
    kpiSaleOrderSigned.value = []
    kpiSaleOrderCreate.value = []

    const body = buildBody()

    const kpiSigned = kpiPost<KpiResponse[]>('/kpi/saleOrder/signed', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiSaleOrderSigned.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorSaleOrderSigned.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingSaleOrderSignedKpi.value = false
      })

    const kpiCreate = kpiPost<KpiResponse[]>('/kpi/saleOrder/create', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiSaleOrderCreate.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorSaleOrderCreate.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingSaleOrderCreateKpi.value = false
      })

    await Promise.all([kpiCreate, kpiSigned])
    if (!signal.aborted) loadedAt.value.saleOrder = Date.now()
    if (controllers.saleOrder === controller) controllers.saleOrder = null
  }

  const getOpportunityKpi = async () => {
    cancelTab('opportunity')
    const controller = new AbortController()
    controllers.opportunity = controller
    const { signal } = controller

    loadingOpportunityWonKpi.value = true
    loadingOpportunityCreatedKpi.value = true
    errorOpportunityWon.value = null
    errorOpportunityCreated.value = null
    kpiOpportunityWon.value = []
    kpiOpportunityCreated.value = []

    const body = buildBody()

    const kpiWon = kpiPost<KpiResponse[]>('/kpi/opportunity/win', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiOpportunityWon.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorOpportunityWon.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingOpportunityWonKpi.value = false
      })

    const kpiCreated = kpiPost<KpiResponse[]>('/kpi/opportunity/created', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiOpportunityCreated.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorOpportunityCreated.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingOpportunityCreatedKpi.value = false
      })

    await Promise.all([kpiWon, kpiCreated])
    if (!signal.aborted) loadedAt.value.opportunity = Date.now()
    if (controllers.opportunity === controller) controllers.opportunity = null
  }

  const getEventKpi = async () => {
    cancelTab('event')
    const controller = new AbortController()
    controllers.event = controller
    const { signal } = controller

    loadingEventCreatedKpi.value = true
    loadingEventDoneKpi.value = true
    loadingEventCampaignKpi.value = true
    errorEventCreated.value = null
    errorEventDone.value = null
    errorEventCampaign.value = null
    kpiEventCreated.value = []
    kpiEventDone.value = []
    kpiEventCampaign.value = []

    const body = buildBody()

    const kpiCreate = kpiPost<KpiResponse[]>('/kpi/calendar/created', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiEventCreated.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorEventCreated.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingEventCreatedKpi.value = false
      })

    const kpiDone = kpiPost<KpiResponse[]>('/kpi/calendar/done', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiEventDone.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorEventDone.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingEventDoneKpi.value = false
      })

    const kpiCampaign = kpiPost<KpiResponse[]>('/kpi/calendar/campaign', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiEventCampaign.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorEventCampaign.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingEventCampaignKpi.value = false
      })

    await Promise.all([kpiCreate, kpiDone, kpiCampaign])
    if (!signal.aborted) loadedAt.value.event = Date.now()
    if (controllers.event === controller) controllers.event = null
  }

  const getCallKpi = async () => {
    cancelTab('call')
    const controller = new AbortController()
    controllers.call = controller
    const { signal } = controller

    loadingCallCreatedKpi.value = true
    errorCallCreated.value = null
    kpiCallCreated.value = []

    const body = buildBody()

    const kpiCreate = kpiPost<KpiResponse[]>('/kpi/call/created', body, signal)
      .then((response) => {
        if (!signal.aborted) kpiCallCreated.value = response
      })
      .catch((error) => {
        if (isAbortError(error)) return
        console.error(error)
        if (!signal.aborted) errorCallCreated.value = GENERIC_ERROR
      })
      .finally(() => {
        if (!signal.aborted) loadingCallCreatedKpi.value = false
      })

    await kpiCreate
    if (!signal.aborted) loadedAt.value.call = Date.now()
    if (controllers.call === controller) controllers.call = null
  }

  // Fetches the data for a given tab. Skips if cached & fresh, unless `force`
  // is set. If a fetch is already in flight for this tab, returns its promise
  // instead of cancelling and restarting — so clicking a tab twice (or coming
  // back to it before the first request finishes) reuses the original fetch.
  const fetchKpiForTab = (tab: KpiTab, options: { force?: boolean } = {}): Promise<void> => {
    if (!options.force && isCacheFresh(tab)) return Promise.resolve()
    const existing = inflight[tab]
    if (existing) return existing

    let promise: Promise<void>
    switch (tab) {
      case 'saleOrder':
        promise = getSaleOrderKpi()
        break
      case 'opportunity':
        promise = getOpportunityKpi()
        break
      case 'event':
        promise = getEventKpi()
        break
      case 'call':
        promise = getCallKpi()
        break
    }
    inflight[tab] = promise
    promise.finally(() => {
      if (inflight[tab] === promise) inflight[tab] = null
    })
    return promise
  }

  // Loads the priority tab first (awaited), then kicks off the remaining tabs
  // in parallel as background prefetch. Resolves once everything is done.
  const fetchAllTabsWithPriority = async (
    priority: KpiTab,
    options: { force?: boolean } = {},
  ) => {
    await fetchKpiForTab(priority, options)
    const others = KPI_TABS.filter((t) => t !== priority)
    await Promise.all(others.map((t) => fetchKpiForTab(t, options)))
  }

  const getMyKpisOpportunities = async () => {
    loadingKpiOpp.value = true

    try {
      kpisOpportunities.value = (await legacyApi.getMyKpisOpportunities()) || []
    } catch (err) {
      console.error('Erreur lors de la récupération des KPI :', err)
      errorKpi.value = 'Impossible de charger les KPI.'
    } finally {
      loadingKpiOpp.value = false
    }
  }

  const initKpiTeam = async () => {
    try {
      // GET /kpi/me/odoo-team : moi + mes managés avec l'ID Odoo de LA source
      // résolue côté serveur (@OdooSource du user connecté). On ne reconstruit
      // plus l'équipe ici avec une clé `odoo16Id` codée en dur, qui écartait les
      // users synchronisés sur une autre source (odoo16b, odoo18, ...) et qui
      // pouvait envoyer un ID vers la mauvaise instance Odoo.
      const res = await suiteStore.suiteFetch('/kpi/me/odoo-team', { method: 'GET' })
      if (!res || typeof res === 'boolean' || !res.ok) return

      const json = await res.json()
      const list: Array<{ odooId: number; name: string; email: string }> = Array.isArray(json)
        ? json
        : (json?.data ?? [])

      const team: KpiCommercial[] = list.map((m) => ({
        id: m.odooId,
        name: m.name,
        email: m.email,
      }))

      kpiCommercials.value = team
      usersSelected.value = team.map((c) => c.id)

      // Enrichir les commerciaux du userStore si Odoo n'a pas de subordonnés
      const { useUserStore } = await import('@/stores/useUserStore')
      const userStore = useUserStore()
      userStore.enrichCommercialsFromKpiTeam(kpiCommercials.value)
    } catch (e) {
      console.error('initKpiTeam error', e)
    }
  }

  initKpiTeam()

  return {
    kpisOpportunities,
    kpiSaleOrderCreate,
    kpiSaleOrderSigned,
    kpiOpportunityCreated,
    kpiOpportunityWon,
    kpiEventCreated,
    kpiEventDone,
    kpiEventCampaign,
    kpiCallCreated,

    usersSelected,
    kpiCommercials,

    // Expose date range
    startDate,
    endDate,
    viewType,
    activeTab,

    loadingKpiOpp,
    loadingSaleOrderCreateKpi,
    loadingSaleOrderSignedKpi,
    loadingOpportunityWonKpi,
    loadingOpportunityCreatedKpi,
    loadingEventCreatedKpi,
    loadingEventDoneKpi,
    loadingEventCampaignKpi,
    loadingCallCreatedKpi,
    isKpiLoading,

    errorKpi,
    errorSaleOrderCreate,
    errorSaleOrderSigned,
    errorOpportunityWon,
    errorOpportunityCreated,
    errorEventCreated,
    errorEventDone,
    errorEventCampaign,
    errorCallCreated,

    // Expose date range functions
    initializeDateRange,

    getMyKpisOpportunities,
    getSaleOrderKpi,
    getOpportunityKpi,
    getEventKpi,
    getCallKpi,
    fetchKpiForTab,
    fetchAllTabsWithPriority,
    invalidateCaches,

    categoryColors,
  }
})
